import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { FilesService } from '@careapp-group/ng-sdk-v2-new';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions/index';
import { getAuthenticationState } from '../auth/auth.selectors';
import * as selectors from '../reducers/index';
import * as actions from './file-upload.actions';
import { RollbarService } from 'angular-rollbar';

@Injectable()
export class FileUploadEffects {
  @Effect({ dispatch: false })
  uploadStart = this.actions$.pipe(
    ofType(actions.RequestFileUploadStartAction),
    withLatestFrom(
      this.store.select(getAuthenticationState),
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getSelectedId(selectors.TENANTS))
    ),
    tap(async ([source, state, organisationId, tenantId]) => {
      const target = await this.filesApi
        .requestUpload({
          uploadRequestDto: {
            tenantId,
            organisationId,
            mimeType: source.file.type,
            purpose: source.purpose,
            modelName: source.modelName,
            name: source.file.name,
            size: source.file.size,
          },
        })
        .toPromise();
      this.putFile(target.uploadUrl, source.file)
        .pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(actions.RequestFileUploadCancelAction),
              filter((s) => {
                return s.id === source.id;
              })
            )
          ),
          catchError((event) => {
            this.errorLogger?.error(event);
            let error = 'Failed to upload - an unknown error has ocurred';
            if (event.status === 413) {
              error = event.error.error.message;
            }
            return of(
              actions.ResponseFileUploadErrorAction({
                id: source.id,
                error,
              })
            );
          })
        )
        .subscribe(async (event: any) => {
          switch (event.type) {
            case HttpEventType.Response:
              await this.filesApi.setComplete({ id: target.file.id }).toPromise();
              return this.store.dispatch(
                actions.ResponseFileUploadCompleteAction({
                  id: source.id,
                  response: target.file,
                  uploadType: source.uploadType,
                })
              );
            case HttpEventType.Sent:
            case HttpEventType.UploadProgress:
            case HttpEventType.ResponseHeader:
            case HttpEventType.DownloadProgress:
              return this.store.dispatch(
                actions.ResponseFileUploadProgressAction({
                  id: source.id,
                  progress: this.progress(event),
                })
              );
            case actions.RESPONSE_FILE_UPLOAD_ERROR:
              return this.store.dispatch(event);
          }
          // Some other failure
          this.store.dispatch(
            actions.ResponseFileUploadErrorAction({
              id: source.id,
              error: 'An unknown error has ocurred',
            })
          );
        });
    })
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private http: HttpClient,
    private filesApi: FilesService,
    @Optional() private errorLogger?: RollbarService
  ) {}

  progress(event) {
    const loaded = event.loaded || 0;
    const total = event.total || 100;
    const percent = (100 * loaded) / total;
    return percent;
  }

  buildFormData(file: File) {
    const formData = new FormData();
    formData.append('file[]', file);
    return formData;
  }

  putFile(url: string, file: File) {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this.http.request(
      new HttpRequest('PUT', url, file, {
        reportProgress: true,
        headers,
        responseType: 'text',
      })
    );
  }
}
